import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { PrimengModule } from '@SharedModules/primeng.module';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { AnaliteService } from '@servicios/analite/analite.service';
import { ConfigService } from '@servicios/config.service';
import { formatCode } from 'app/_sharedFuctions/shared-functions';

@Component({
  selector: 'app-table-select-analite',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PrimengModule
  ],
  templateUrl: './table-select-analite.component.html',
  styleUrls: ['./table-select-analite.component.css'],
  providers: [MessageService]
})
export class TableSelectAnaliteComponent implements OnInit, OnDestroy {
  @Input() title: string = 'Seleccionar Determinación';
  @Input() subtitle: string = 'Busque y seleccione la determinación deseada para configurar en el ensayo';
  @Input() showCancelButton: boolean = true;
  @Input() defaultOnlyActive: boolean = true;
  @Input() backButtonLabel: string = 'Volver al formulario';
  @Input() isDialogMode: boolean = false;

  @Output() selectAnalite = new EventEmitter<any>();
  @Output() cancelSelection = new EventEmitter<void>();

  analiteService = inject(AnaliteService);
  configService = inject(ConfigService);
  messageService = inject(MessageService);

  // Pagination & lazy loading state
  analites: any[] = [];
  totalRecords: number = 0;
  pageSize: number = 10;
  first: number = 0;
  currentPage: number = 1;
  loading: boolean = false;
  sortField: string = 'code';
  sortOrder: number = 1;

  // Search & Filter state
  searchQuery: string = '';
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  statusFilter: 'active' | 'all' = 'active';
  statusOptions = [
    { label: 'Solo Activas', value: 'active' },
    { label: 'Todas las determinaciones', value: 'all' }
  ];

  selectedAnalite: any = null;

  constructor(
    @Optional() public ref?: DynamicDialogRef,
    @Optional() public config?: DynamicDialogConfig
  ) {
    if (this.config?.data?.isSelectAnaliteDialog) {
      this.isDialogMode = true;
      if (this.config.data.title) this.title = this.config.data.title;
      if (this.config.data.subtitle) this.subtitle = this.config.data.subtitle;
      if (this.config.data.defaultOnlyActive !== undefined) {
        this.statusFilter = this.config.data.defaultOnlyActive ? 'active' : 'all';
      }
    }
  }

  ngOnInit(): void {
    if (this.defaultOnlyActive) {
      this.statusFilter = 'active';
    }

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged()
    ).subscribe((term) => {
      this.first = 0;
      this.currentPage = 1;
      this.loadAnalites();
    });

    this.loadAnalites();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value?.trim() || '');
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.first = 0;
    this.currentPage = 1;
    this.loadAnalites();
  }

  onStatusFilterChange(): void {
    this.first = 0;
    this.currentPage = 1;
    this.loadAnalites();
  }

  async loadAnalites(): Promise<void> {
    this.loading = true;
    try {
      const filters: any = {};
      if (this.statusFilter === 'active') {
        filters.active = { value: true, matchMode: 'equals' };
      }

      const res = await this.analiteService.getPaginated({
        page: this.currentPage,
        limit: this.pageSize,
        search: this.searchQuery?.trim() || undefined,
        filters: Object.keys(filters).length ? filters : undefined,
        sortBy: this.sortField,
        sortOrder: this.sortOrder > 0 ? 'asc' : 'desc'
      });

      this.analites = (res.response || []).map((a: any) => ({
        ...a,
        formattedCode: formatCode(a.code)
      }));
      this.totalRecords = res.total || 0;
    } catch (error) {
      console.error('Error cargando determinaciones:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las determinaciones'
      });
    } finally {
      this.loading = false;
    }
  }

  onLazyLoad(event: any): void {
    if (event.rows) {
      this.pageSize = event.rows;
    }
    if (event.first !== undefined) {
      this.first = event.first;
      this.currentPage = Math.floor(event.first / (event.rows || this.pageSize)) + 1;
    }
    if (event.sortField) {
      this.sortField = event.sortField;
    }
    if (event.sortOrder !== undefined) {
      this.sortOrder = event.sortOrder;
    }
    this.loadAnalites();
  }

  formatCode(code: any): any {
    return formatCode(code);
  }

  selectItem(analite: any): void {
    if (!analite?.id) return;
    if (!analite.active) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'La determinación seleccionada no se encuentra activa'
      });
      return;
    }

    this.selectAnalite.emit(analite);

    if (this.isDialogMode && this.ref && this.config?.data?.isSelectAnaliteDialog) {
      this.ref.close(analite);
    }
  }

  cancel(): void {
    this.cancelSelection.emit();
    if (this.isDialogMode && this.ref && this.config?.data?.isSelectAnaliteDialog) {
      this.ref.close(null);
    }
  }
}
